import { format, parseISO } from 'date-fns';
import { sortBy } from 'lodash';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { ServingSizeIcon } from '@hungryroot/components-pairing-card';
import { useCustomerServingSize } from '../../../../../../api/v2/customers/[id]/pricing_config/get/get-customer-pricing-config';
import { useTagGroup, tagGroups } from '@hungryroot/tags';

import styles from './Header.module.css';

/*
This is the reusable header for all Recipe/Pairing cards
props:
pairingData: pairing data returned by the getPairing ajax request
showRating: Boolean to either show rating or the dishtype thumbnail
*/

const Header = ({
  pairingData,
  showRating,
  showServings,
  hideIcon,
  orderDate,
  isEdited,
  className,
  customerId,
}) => {
  const { data: spicyTags } = useTagGroup(tagGroups.spiceLevel);
  const servingSize = customerId && useCustomerServingSize();

  if (!spicyTags) {
    return null;
  }

  return (
    <div className={`header hr-mb-24 ${className}`}>
      <div className="dishtype-and-infos">
        {pairingData.is_featured ? (
          <div className="chef-pick hr-mb-8">
            <img
              className="chef-hat"
              src={`/public/img/pairing/chef_hat.svg`}
              alt="Chef's Pick icon"
            />
            <span className="hr-paragraph-bold">Chef's Pick</span>
          </div>
        ) : null}
        {pairingData.name ? (
          <h1
            data-testid="rdp-pairing-title"
            className={clsx('hr-title-md hr-mb-8', styles['weight-heavy'])}
          >
            {pairingData.name}
            {isEdited && ' (edited)'}
          </h1>
        ) : (
          <div className="hr-title-md hr-mb-8">
            {pairingData.dish_type ? pairingData.dish_type.name : ''}
          </div>
        )}
        <div className="pairing-infos hr-subtitle-md">
          {orderDate ? (
            <span>
              Received on
              <span className="order-date">
                {format(parseISO(orderDate), 'M/d')}
              </span>
            </span>
          ) : (
            <>
              {pairingData.cooking_time ? (
                <span>
                  <span className="time"></span>
                  {`${pairingData.cooking_time} min.`}
                </span>
              ) : null}
              {pairingData.calories ? (
                <span>
                  <span className="calories"></span>
                  {`${pairingData.calories} cal.`}
                </span>
              ) : null}
              {showServings ? (
                <ServingSizeIcon
                  pairingServingSize={pairingData.servings}
                  customerPlanServingSize={servingSize}
                  replaceIconAndText={true}
                />
              ) : null}
              {pairingData.spicy_level !== undefined && spicyTags ? (
                <span data-testid="rdp-spicy" className="spicy-data">
                  <img
                    className="spicy-img"
                    src={`/public/img/modals/pepper.svg`}
                    alt="Spicy icon"
                  />
                  {`${
                    sortBy(spicyTags.tags || [], ['id'])[
                      pairingData.spicy_level
                    ]?.name
                  }`}
                </span>
              ) : null}
            </>
          )}
        </div>
      </div>
      {showRating || hideIcon || !pairingData.dish_type ? null : (
        <img
          src={pairingData.dish_type.card_image_url}
          alt={pairingData.name}
        />
      )}
    </div>
  );
};

Header.propTypes = {
  pairingData: PropTypes.object.isRequired,
  showRating: PropTypes.bool,
  showServings: PropTypes.bool,
};

Header.defaultProps = {
  showServings: true,
};

export default Header;
